import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  FiPlus, FiEdit2, FiTrash2, FiX, FiTag, FiPercent, 
  FiDollarSign, FiCalendar, FiUsers, FiCopy, FiCheck,
  FiGift, FiTrendingUp
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PromoCodesPage() {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_amount: '',
    max_uses: '',
    max_uses_per_customer: '1',
    valid_from: '',
    valid_until: '',
    is_active: true,
    applies_to: 'all' // all, particulier, entreprise
  });

  useEffect(() => {
    loadPromoCodes();
  }, []);

  const loadPromoCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/promo-codes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPromoCodes(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error loading promo codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.code || !formData.discount_value) {
      toast.error('Code et valeur de réduction requis');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = selectedCode 
        ? `${API_URL}/api/promo-codes/${selectedCode.id}`
        : `${API_URL}/api/promo-codes`;
      
      const response = await fetch(url, {
        method: selectedCode ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          discount_value: parseFloat(formData.discount_value),
          min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : null,
          max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
          max_uses_per_customer: formData.max_uses_per_customer ? parseInt(formData.max_uses_per_customer) : 1
        })
      });

      if (response.ok) {
        toast.success(selectedCode ? 'Code promo mis à jour' : 'Code promo créé');
        setShowModal(false);
        resetForm();
        loadPromoCodes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce code promo ?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/promo-codes/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Code promo supprimé');
        loadPromoCodes();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (code) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/promo-codes/${code.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...code, is_active: !code.is_active })
      });

      if (response.ok) {
        toast.success(code.is_active ? 'Code désactivé' : 'Code activé');
        loadPromoCodes();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const editCode = (code) => {
    setSelectedCode(code);
    setFormData({
      code: code.code || '',
      description: code.description || '',
      discount_type: code.discount_type || 'percentage',
      discount_value: code.discount_value?.toString() || '',
      min_order_amount: code.min_order_amount?.toString() || '',
      max_uses: code.max_uses?.toString() || '',
      max_uses_per_customer: code.max_uses_per_customer?.toString() || '1',
      valid_from: code.valid_from?.slice(0, 10) || '',
      valid_until: code.valid_until?.slice(0, 10) || '',
      is_active: code.is_active !== false,
      applies_to: code.applies_to || 'all'
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setSelectedCode(null);
    setFormData({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: '',
      min_order_amount: '',
      max_uses: '',
      max_uses_per_customer: '1',
      valid_from: '',
      valid_until: '',
      is_active: true,
      applies_to: 'all'
    });
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = 'SMILE';
    for (let i = 0; i < 5; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, code });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
    toast.success('Code copié !');
  };

  const isExpired = (validUntil) => {
    if (!validUntil) return false;
    return new Date(validUntil) < new Date();
  };

  const stats = {
    total: promoCodes.length,
    active: promoCodes.filter(c => c.is_active && !isExpired(c.valid_until)).length,
    totalUsed: promoCodes.reduce((sum, c) => sum + (c.times_used || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="promo-codes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Codes Promo</h1>
          <p className="text-slate-500 mt-1">Gérez vos codes promotionnels</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-amber-600 hover:bg-amber-700">
          <FiPlus className="mr-2 h-4 w-4" /> Nouveau code
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FiTag className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-slate-500">Codes créés</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FiCheck className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.active}</p>
                <p className="text-sm text-slate-500">Codes actifs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FiTrendingUp className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalUsed}</p>
                <p className="text-sm text-slate-500">Utilisations totales</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Codes List */}
      {promoCodes.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FiGift className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="font-medium text-slate-700 mb-2">Aucun code promo</h3>
            <p className="text-slate-500 mb-4">Créez votre premier code promotionnel</p>
            <Button onClick={() => { resetForm(); setShowModal(true); }}>
              <FiPlus className="mr-2" /> Créer un code
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {promoCodes.map(code => {
            const expired = isExpired(code.valid_until);
            const usesLeft = code.max_uses ? code.max_uses - (code.times_used || 0) : null;
            const isAvailable = code.is_active && !expired && (usesLeft === null || usesLeft > 0);
            
            return (
              <Card key={code.id} className={`transition-all ${!isAvailable ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Code Display */}
                      <div className="flex items-center gap-2">
                        <code className="text-lg font-bold bg-slate-100 px-3 py-1 rounded-lg">
                          {code.code}
                        </code>
                        <button 
                          onClick={() => copyCode(code.code)}
                          className="p-1 hover:bg-slate-100 rounded"
                        >
                          {copiedCode === code.code ? (
                            <FiCheck className="h-4 w-4 text-green-600" />
                          ) : (
                            <FiCopy className="h-4 w-4 text-slate-400" />
                          )}
                        </button>
                      </div>

                      {/* Discount */}
                      <div className="flex items-center gap-1 text-lg font-semibold text-green-600">
                        {code.discount_type === 'percentage' ? (
                          <>-{code.discount_value}%</>
                        ) : (
                          <>-{code.discount_value}€</>
                        )}
                      </div>

                      {/* Status Badges */}
                      <div className="flex gap-2">
                        {!code.is_active && (
                          <Badge variant="secondary">Désactivé</Badge>
                        )}
                        {expired && (
                          <Badge variant="destructive">Expiré</Badge>
                        )}
                        {usesLeft !== null && usesLeft <= 0 && (
                          <Badge variant="secondary">Épuisé</Badge>
                        )}
                        {isAvailable && (
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Actif</Badge>
                        )}
                        {code.applies_to === 'particulier' && (
                          <Badge variant="outline">Particuliers</Badge>
                        )}
                        {code.applies_to === 'entreprise' && (
                          <Badge variant="outline">Entreprises</Badge>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={code.is_active}
                        onCheckedChange={() => handleToggleActive(code)}
                      />
                      <Button variant="ghost" size="sm" onClick={() => editCode(code)}>
                        <FiEdit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(code.id)} className="text-red-500 hover:text-red-700">
                        <FiTrash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-slate-500">
                    {code.description && (
                      <div className="col-span-2">
                        <span className="text-slate-400">Description:</span> {code.description}
                      </div>
                    )}
                    {code.valid_until && (
                      <div>
                        <FiCalendar className="inline h-3 w-3 mr-1" />
                        Expire: {code.valid_until.slice(0, 10)}
                      </div>
                    )}
                    {code.max_uses && (
                      <div>
                        <FiUsers className="inline h-3 w-3 mr-1" />
                        {code.times_used || 0}/{code.max_uses} utilisations
                      </div>
                    )}
                    {code.min_order_amount && (
                      <div>
                        <FiDollarSign className="inline h-3 w-3 mr-1" />
                        Min: {code.min_order_amount}€
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">
                  {selectedCode ? 'Modifier le code' : 'Nouveau code promo'}
                </h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded">
                  <FiX className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Code */}
                <div>
                  <Label>Code promo *</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={formData.code}
                      onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="SMILE2026"
                      className="font-mono uppercase"
                    />
                    <Button type="button" variant="outline" onClick={generateCode}>
                      Générer
                    </Button>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <Label>Description (optionnel)</Label>
                  <Input
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Ex: Offre de lancement"
                    className="mt-1"
                  />
                </div>

                {/* Discount Type & Value */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Type de réduction *</Label>
                    <Select
                      value={formData.discount_type}
                      onValueChange={v => setFormData({ ...formData, discount_type: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Pourcentage (%)</SelectItem>
                        <SelectItem value="fixed">Montant fixe (€)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Valeur *</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        value={formData.discount_value}
                        onChange={e => setFormData({ ...formData, discount_value: e.target.value })}
                        placeholder={formData.discount_type === 'percentage' ? '10' : '50'}
                        min="0"
                      />
                      <span className="text-slate-500">
                        {formData.discount_type === 'percentage' ? '%' : '€'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Applies to */}
                <div>
                  <Label>S'applique à</Label>
                  <Select
                    value={formData.applies_to}
                    onValueChange={v => setFormData({ ...formData, applies_to: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les clients</SelectItem>
                      <SelectItem value="particulier">Particuliers uniquement</SelectItem>
                      <SelectItem value="entreprise">Entreprises uniquement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Min Order Amount */}
                <div>
                  <Label>Montant minimum de commande (optionnel)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number"
                      value={formData.min_order_amount}
                      onChange={e => setFormData({ ...formData, min_order_amount: e.target.value })}
                      placeholder="0"
                      min="0"
                    />
                    <span className="text-slate-500">€</span>
                  </div>
                </div>

                {/* Usage Limits */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Limite d'utilisations totales</Label>
                    <Input
                      type="number"
                      value={formData.max_uses}
                      onChange={e => setFormData({ ...formData, max_uses: e.target.value })}
                      placeholder="Illimité"
                      min="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Limite par client</Label>
                    <Input
                      type="number"
                      value={formData.max_uses_per_customer}
                      onChange={e => setFormData({ ...formData, max_uses_per_customer: e.target.value })}
                      placeholder="1"
                      min="1"
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Validity Period */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date de début (optionnel)</Label>
                    <Input
                      type="date"
                      value={formData.valid_from}
                      onChange={e => setFormData({ ...formData, valid_from: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Date de fin (optionnel)</Label>
                    <Input
                      type="date"
                      value={formData.valid_until}
                      onChange={e => setFormData({ ...formData, valid_until: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Active Toggle */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={v => setFormData({ ...formData, is_active: v })}
                  />
                  <div>
                    <p className="font-medium">Code actif</p>
                    <p className="text-sm text-slate-500">Le code peut être utilisé par les clients</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                  Annuler
                </Button>
                <Button onClick={handleSave} className="flex-1 bg-amber-600 hover:bg-amber-700">
                  {selectedCode ? 'Mettre à jour' : 'Créer le code'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
